import { Hidden, Modal } from '@mui/material'
import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import detectIncognito from 'detectincognitojs'
import mixpanel from 'mixpanel-browser'
import { useRouter } from 'next/router'
import React, { Dispatch, useEffect, useState } from 'react'
import { TailSpin } from 'react-loading-icons'
import { useDispatch, useSelector } from 'react-redux'
import { FilterOptions } from 'src/components/FilterOptions/FilterOptions'
import { Filter } from 'src/components/global/filter'
import { LoadingAnimation } from 'src/components/global/LoadingAnimation'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { SortbyDropdown } from 'src/components/global/SortbyDropdown'
import { Pagination } from 'src/components/Pagination/Pagination'
import { VendorList } from 'src/components/VendorList/VendorList'
import { AMPLIFY_USER_POOL_APP_CLIENT_ID, MIXPANEL_ENABLED, SHOW_FREE_SEARCH, THEME } from 'src/config/config'
import {
  IEnum_Solution_Type,
  ISolutionListItemFragment,
  ISubTopicItemFragment,
  ITopicItemFragment,
  useSearchSolutionsLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { showToast } from 'src/store/actions/toastAction'
import { AppState } from 'src/store/types'
import { CompareInfos } from 'src/store/types/compare'
import { FilterOption, TaxonomyType } from 'src/types'

import { CompareModalContent } from '../CompareModalContent/CompareModalContent'
import useSearchQuery from '../CustomHooks/SearchQuery'
import { getFingerprint } from '../CustomHooks/useFingerprint'
import Drawer from '../global/Drawer/Drawer'
import { PremiumModal } from '../global/PremiumModal'
import FreeSearchPopup from '../Popup/FreeSearchPopup'

type VendorPaginationProp = {
  filterProps: FilterOption[]
  topic?: ITopicItemFragment
  subTopic?: ISubTopicItemFragment
  solutionType?: IEnum_Solution_Type
  preAppliedFilters?: { [key: string]: string }
  includeCompany: boolean
  changeTotalCount?: (total: number) => void
  setFilterHeight?: (value: boolean) => void
}

export const CollectionVendorPagination: React.FC<VendorPaginationProp> = ({
  filterProps,
  topic,
  includeCompany,
  preAppliedFilters = {},
  changeTotalCount,
  setFilterHeight,
}) => {
  const router = useRouter()
  const [isNonLogin, setIsNonLogin] = useState(false)
  const [isFreeSearch, setIsFreeSearch] = useState<boolean | null>(null)
  const { getSearchQuery } = useSearchQuery()
  const { updateQueryParams } = useUpdateQueryParams()

  const [showPremiumModal, setShowPremiumModal] = useState(false)

  const handleClosePremiumModal = () => {
    setShowPremiumModal(false)
  }

  const { session } = useAuthenticated()
  const confirmed = session?.user?.confirmed || false
  const token = session?.user.idToken || ''
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const closeDrawer = React.useCallback(() => setIsVisible(false), [])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [items, setItems] = useState<ISolutionListItemFragment[]>([])
  const [postsPerPage] = useState(10)
  const [showFilterDlg, setShowFilterDlg] = useState(false)
  const compareInfos: CompareInfos = useSelector((state: AppState) => state.compareInfos)
  const [loadSolutions, { loading, data: searchSolutionsData }] = useSearchSolutionsLazyQuery({
    fetchPolicy: 'no-cache',
  })
  const dispatch: Dispatch<any> = useDispatch()

  const getNamesFromIds = (type: string, ids?: string) => {
    if (!ids) return []
    const taxonomy = filterProps.find((item) => item.title === type)
    const idArray = ids.split(',')
    const result: string[] = []
    if (taxonomy) {
      idArray.map((id) => {
        const res = taxonomy.data.find((item) => item.id === id)
        if (res) {
          result.push(res.name)
        }
      })
    }
    return [...new Set(result)]
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const lastAuthUser = localStorage.getItem(
        `CognitoIdentityServiceProvider.${AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`,
      )
      setIsNonLogin(!lastAuthUser) // Safely set the boolean value
    }
  }, [])

  useEffect(() => {
    if (changeTotalCount) changeTotalCount(totalCount)
  }, [totalCount])

  const fetchData = async () => {
    const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
    const indexOfLastPost = curPage * postsPerPage
    const indexOfFirstPost = indexOfLastPost - postsPerPage
    const filters = router.query
    let selection: string[] = []
    if (filters.productType) {
      const paths = (filters.productType as string).split('_')
      const checks = paths[0].split(',')

      checks.forEach((check) => {
        const subTopicDocumentId = check.split('@@@@')[1]
        selection.push(subTopicDocumentId)
      })
    }
    selection = [...new Set(selection)]
    const newProductType = selection.join(',')
    const baseQuery = {
      ...preAppliedFilters,
      offset: indexOfFirstPost,
      limit: postsPerPage,
      keyword: filters !== undefined ? (filters.keyword as string) : undefined,
      audiences: filters !== undefined ? (filters.audiences as string) : undefined,
      practiceAreas: filters !== undefined ? (filters.practiceAreas as string) : undefined,
      languages: filters !== undefined ? (filters.languages as string) : undefined,
      deployments: filters !== undefined ? (filters.deployments as string) : undefined,
      attributes: filters !== undefined ? (filters.attributes as string) : undefined,
      regionsServed: filters !== undefined ? (filters.regionsServed as string) : undefined,
      sort: filters !== undefined ? (filters.sort as string) || 'Default' : 'Default',
      hqs: filters !== undefined ? (filters.hqs as string) : undefined,
      subTopic: filters !== undefined ? (newProductType as string) : undefined,
      // type: IEnum_Solution_Type.Product,
      includeCompany,
      sponsorLevels: filters !== undefined ? (filters.sponsorLevels as string) : undefined,
      integrations: filters !== undefined ? (filters.integrations as string) : undefined,
    }
    const customAccessQuery: string = getSearchQuery(filters, newProductType)
    const fingerprint = await getFingerprint()
    const browser = await detectIncognito()
    const query =
      SHOW_FREE_SEARCH && fingerprint && customAccessQuery != ''
        ? {
            ...baseQuery,
            accessFreeSearch: true,
            uniqueAddress: !browser?.isPrivate && browser?.browserName !== '' ? fingerprint.toString() : '0',
            customAccessQuery: customAccessQuery,
          }
        : baseQuery

    if (MIXPANEL_ENABLED) {
      mixpanel.track('search_content', {
        keyword: query.keyword,
        audiences: getNamesFromIds(TaxonomyType.Audience, query.audiences),
        practiceAreas: getNamesFromIds(TaxonomyType.PracticeArea, query.practiceAreas),
        languages: getNamesFromIds(TaxonomyType.Language, query.languages),
        deployments: getNamesFromIds(TaxonomyType.Deployment, query.deployments),
        attributes: getNamesFromIds(TaxonomyType.Attribute, query.attributes),
        regionsServed: getNamesFromIds(TaxonomyType.RegionsServed, query.regionsServed),
        topic: topic ? [topic?.name] : [],
        hqs: getNamesFromIds(TaxonomyType.HQ, query.hqs),
      })
    }

    loadSolutions({ variables: { query }, fetchPolicy: 'no-cache' })
  }

  useEffect(() => {
    if (!router.isReady) return
    fetchData()
  }, [currentPage, router.isReady, router.query, token])

  const refreshFollowVendorList = async () => {
    void fetchData()
  }

  useEffect(() => {
    if (loading) {
      return
    }
    const curPage = router.query.page !== undefined ? parseInt(router.query.page as string) : 1
    setCurrentPage(curPage)

    let isFreeSearch = null
    if (
      SHOW_FREE_SEARCH &&
      (searchSolutionsData?.searchSolutions?.isFreeSearch == true ||
        searchSolutionsData?.searchSolutions?.isFreeSearch == false)
    ) {
      isFreeSearch = searchSolutionsData?.searchSolutions?.isFreeSearch
    }
    if (typeof setFilterHeight == 'function') {
      if (isFreeSearch == false) {
        setFilterHeight(true)
      } else {
        setFilterHeight(false)
      }
    }
    setIsFreeSearch(isFreeSearch)
    const items: ISolutionListItemFragment[] = (searchSolutionsData?.searchSolutions?.solutions || []).filter(
      (vendor): vendor is ISolutionListItemFragment => !!vendor,
    )
    setItems(items)
    setTotalCount(searchSolutionsData?.searchSolutions?.total || 0)

    //arv: show toast message for search limit

    if (
      !confirmed &&
      searchSolutionsData &&
      searchSolutionsData?.searchSolutions?.isFreeSearch == true &&
      searchSolutionsData?.searchSolutions?.isFreeSearchLeft == 1
    ) {
      dispatch(showToast('This is your last free search for the week. ', 'non_r_last_free_search'))
    }
    if (
      confirmed &&
      searchSolutionsData &&
      searchSolutionsData?.searchSolutions?.isFreeSearch == true &&
      searchSolutionsData?.searchSolutions?.isFreeSearchLeft == 1
    ) {
      dispatch(showToast('This is your last free search for the week. ', 'r_last_free_search'))
    }
  }, [loading, searchSolutionsData])

  useEffect(() => {
    if (compareInfos.solutions.length > 0) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [compareInfos])

  const navigateToPage = (page: number) => {
    updateQueryParams({ page: page.toString() }, true)
  }

  // Change page
  const paginateFront = () => {
    const lastpage = totalCount / postsPerPage
    if (currentPage < lastpage) {
      setCurrentPage(currentPage + 1)
      navigateToPage(currentPage + 1)
    }
  }
  const paginateBack = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      navigateToPage(currentPage - 1)
    }
  }
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber)
    navigateToPage(pageNumber)
  }

  // const exportSolutions = async () => {
  //   if (!isPremium) {
  //     setShowPremiumModal(true)
  //   } else {
  //     const res = await exportTopicsCsv(
  //       {
  //         topic: solutionType === IEnum_Solution_Type.Product ? topic?.id : undefined,
  //         subTopic: subTopic?.id || undefined,
  //         type: solutionType,
  //       },
  //       token,
  //     )

  //     if (res) {
  //       const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
  //       const link = document.createElement('a')
  //       link.href = url
  //       link.setAttribute('download', 'Solutions_Export.csv')
  //       document.body.appendChild(link)
  //       link.click()
  //     }
  //   }
  // }
  const freeSearchWithAllCTA = (
    <FreeSearchPopup
      heading={`You have reached your final search for the week.`}
      title={<>Register a free account on LTH to continue searching.</>}
      createAccountButton={true}
      premiumSignupButton={false}
      foundSolution={items?.length > 0}
    />
  )

  const freeSearchWithPremiumButton = (
    <FreeSearchPopup
      heading={`You have reached your final free search for the week.`}
      title={
        <>
          Sign up to LTH <span className="text-[#3329EF]">Premium</span> for unlimited searches.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
      foundSolution={items?.length > 0}
    />
  )
  return (
    <div className="flex flex-col flex-1">
      {compareInfos.solutions && compareInfos.solutions.length ? (
        <Drawer duration={250} hideScrollbars={true} onClose={closeDrawer} isVisible={isVisible}>
          <CompareModalContent onClose={closeDrawer} solutionType={compareInfos.solutions[0].solutionType} />
        </Drawer>
      ) : null}
      <div className={cn('md:pl-0 px-[20px] pt-5 md:pt-10', { '!pt-3': THEME === 'ilta' })}>
        <FilterOptions filterOptions={filterProps} className="md:my-[1rem] md:mb-0 mb-[20px]" />
        <div className="flex flex-row justify-between md:items-center h-16 mb-[1rem] font-primary font-medium">
          <Hidden mdDown>
            {loading && (
              <div className="flex items-center">
                <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
              </div>
            )}
            {!loading && (
              <div className="font-primary font-medium">
                {(totalCount ?? 0) > 0
                  ? `${totalCount ?? 0} Solution${(totalCount ?? 0) > 1 ? 's' : ''} Found`
                  : `No solution found`}
              </div>
            )}
            {/* <div className="flex flex-row items-center space-x-4">
              <SortbyDropdown className="w-54 font-primary font-medium" type="solutions" />
              <button
                className={`${
                  isPremium ? 'bg-navy text-white' : 'bg-gray-300 text-slate-900'
                } py-2.5 px-4 rounded-full text-[14px] flex flex-row justify-center items-center`}
                onClick={exportSolutions}
              >
                Export Solutions <IonIcon name="download" className="ml-2" />
              </button>
            </div> */}
            <SortbyDropdown className="w-54 font-primary font-medium" type="solutions" />
          </Hidden>
          <Hidden mdUp>
            <div className="flex flex-col">
              <Filter onClickFilter={() => setShowFilterDlg(true)} />
              <div className="font-primary text-[12px] text-darkgray h-[20px] mb-[20px] mt-[3px]">
                {(totalCount ?? 0) > 0
                  ? `${totalCount ?? 0} Solution${(totalCount ?? 0) > 1 ? 's' : ''} Found`
                  : `No solution found`}
              </div>
            </div>
            <SortbyDropdown className="w-54 font-primary font-medium" type="solutions" />
          </Hidden>
        </div>
        {loading && <LoadingAnimation></LoadingAnimation>}
        {!loading && (
          <div>
            <VendorList items={items} topic={topic} refreshFollowVendorList={refreshFollowVendorList} />
            {isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithAllCTA}
            {!isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithPremiumButton}
            {totalCount > 0 && (!SHOW_FREE_SEARCH || (SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) && (
              <Pagination
                postsPerPage={postsPerPage}
                totalPosts={totalCount}
                paginateBack={paginateBack}
                paginateFront={paginateFront}
                paginate={paginate}
                currentPage={currentPage}
                className="md:pt-[1.875rem] pt-[0.875rem]"
              />
            )}
          </div>
        )}
      </div>
      <Modal open={showFilterDlg}>
        <div className="flex flex-col w-full h-full bg-white p-[17px]">
          <div className="flex flex-row mb-[13px]">
            <p className="font-secondary font-bold text-[20px] text-darkgray items-center">Filter</p>
            <div className="ml-auto" onClick={() => setShowFilterDlg(false)}>
              <IonIcon name="close-outline" className="!h-[30px] !w-[30px] text-[black]" />
            </div>
          </div>
          <SearchField className="mb-[23px]" />
          <Sidebar filterOptions={filterProps} defaultSelectFilterIdx={1} />
          <button
            className="text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy mt-auto mb-[15px] h-[50px] !important"
            onClick={() => setShowFilterDlg(false)}
          >
            Apply Filters
          </button>
        </div>
      </Modal>

      <PremiumModal origin={origin} isOpen={showPremiumModal} onClose={handleClosePremiumModal} />
    </div>
  )
}
